import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

interface DesktopSheetProps {
  /** Whether the sheet is open */
  open: boolean;
  /** Callback when open state changes */
  onOpenChange: (open: boolean) => void;
  /** Sheet title displayed in the header */
  title?: string;
  /** Sheet content */
  children: React.ReactNode;
  /** Width of the sheet (default: 458px) */
  width?: number;
  /** Whether to show the close button (default: true) */
  showCloseButton?: boolean;
  /** Custom header content (replaces default title) */
  header?: React.ReactNode;
  /** Footer content (sticky at bottom) */
  footer?: React.ReactNode;
  /** z-index for the sheet (default: 100) */
  zIndex?: number;
}

/**
 * Desktop-specific sheet component that slides in from the right.
 * Replaces mobile Drawers on desktop for a better UX.
 */
export const DesktopSheet: React.FC<DesktopSheetProps> = ({
  open,
  onOpenChange,
  title,
  children,
  width = 458,
  showCloseButton = true,
  header,
  footer,
  zIndex = 100,
}) => {
  const sheetRef = useRef<HTMLDivElement>(null);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        onOpenChange(false);
      }
    };

    if (open) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when sheet is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [open, onOpenChange]);

  // Focus trap
  useEffect(() => {
    if (open && sheetRef.current) {
      sheetRef.current.focus();
    }
  }, [open]);

  if (!open) {
    return null;
  }

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/60 transition-opacity duration-300"
        style={{ zIndex }}
        onClick={() => onOpenChange(false)}
        aria-hidden="true"
      />

      {/* Sheet */}
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'sheet-title' : undefined}
        tabIndex={-1}
        className="fixed top-0 right-0 h-full bg-secondary border-l border-border flex flex-col shadow-2xl transition-transform duration-300 ease-out"
        style={{
          width: `${width}px`,
          zIndex: zIndex + 1,
          transform: open ? 'translateX(0)' : `translateX(${width}px)`,
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-secondary sticky top-0 z-10">
          {header || (
            <h2 id="sheet-title" className="text-lg font-semibold text-text-primary">
              {title}
            </h2>
          )}
          {showCloseButton && (
            <button
              onClick={() => onOpenChange(false)}
              className="p-2 rounded-lg hover:bg-tertiary text-text-secondary hover:text-text-primary transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="sticky bottom-0 bg-secondary border-t border-border p-4">
            {footer}
          </div>
        )}
      </div>
    </>
  );
};

/**
 * Hook to determine if we should use DesktopSheet or mobile Drawer
 * based on screen size
 */
export const useSheetOrDrawer = () => {
  // This is a simple implementation - you could expand it
  // to return more specific configuration
  const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 1024;
  return { isDesktop };
};
